import { Button, Menu, MenuButton, MenuItem, MenuList, Popover, PopoverTrigger, useDisclosure } from '@chakra-ui/react';
import { useState } from 'react';
import { HiPlus } from 'react-icons/hi';

import { DataTableFilter, Loosen } from '../utils/typings';
import { CheckboxFilterPopoverContent } from './PopoverContent/CheckboxFilterPopoverContent';
import { DateFilterPopoverContent } from './PopoverContent/DateFilterPopoverContent';
import { RadioFilterPopoverContent } from './PopoverContent/RadioFilterPopoverContent';
import { StringFilterPopoverContent } from './PopoverContent/StringFilterPopoverContent';

type Props<T extends DataTableFilter[]> = {
  options?: Loosen<T>;
  activeFilters: string[];
  handleAddFilter: (option: T[number]) => () => void;
};

export const DataTableAddFilter = <T extends DataTableFilter[]>({
  options = [],
  activeFilters,
  handleAddFilter,
}: Props<T>) => {
  const [popoverFilter, setPopoverFilter] = useState<DataTableFilter | null>(null);
  const { isOpen, onToggle, onClose } = useDisclosure();
  const handleChange = (filter: DataTableFilter) => (value: any) => {
    filter.value = value;
  };

  const handleClose = () => {
    setPopoverFilter(null);
    onClose();
  };

  const handleClick = (filter: DataTableFilter) => () => {
    setPopoverFilter(filter);
    onToggle();
  };

  const renderPopoverContent = (filter: DataTableFilter | null) => {
    if (!filter) return;
    // case "boolean" and "operator" weren't needed as of 1/25/2024
    switch (filter.inputType) {
      case 'string':
        return <StringFilterPopoverContent onChange={handleChange(filter)} onSubmit={handleAddFilter(filter)} />;

      case 'checkbox':
        return (
          <CheckboxFilterPopoverContent
            value={filter?.value as string[]}
            options={filter?.options}
            onChange={handleChange(filter)}
            onSubmit={handleAddFilter(filter)}
          />
        );

      case 'date':
        return (
          <DateFilterPopoverContent
            value={filter?.value as Date}
            onChange={handleChange(filter)}
            onSubmit={handleAddFilter(filter)}
          />
        );

      case 'radio':
        return (
          <RadioFilterPopoverContent
            value={filter?.value as string}
            options={filter?.options}
            onChange={handleChange(filter)}
            onSubmit={handleAddFilter(filter)}
          />
        );
    }
  };

  const renderMenuItem = (option: DataTableFilter) => (
    <MenuItem key={option?.name} isDisabled={activeFilters?.includes(option?.name)} onClick={handleClick(option)}>
      {option?.label}
    </MenuItem>
  );

  return (
    <Popover isOpen={isOpen} onClose={handleClose} placement="bottom-start">
      <Menu>
        <PopoverTrigger>
          <MenuButton as={Button} size="sm" colorScheme="gray" rightIcon={<HiPlus />}>
            Filter
          </MenuButton>
        </PopoverTrigger>
        <MenuList>{options?.map(renderMenuItem)}</MenuList>
      </Menu>
      {renderPopoverContent(popoverFilter)}
    </Popover>
  );
};
